import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const paymentIntentId = searchParams.get('payment_intent');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!paymentIntentId && !sessionId) {
        setError('No payment information found');
        setLoading(false);
        return;
      }

      const endpoint = sessionId
        ? `${API_URL}/api/checkout/session/${sessionId}`
        : `${API_URL}/api/checkout/order/${paymentIntentId}`;

      try {
        const token = user ? await user.getIdToken() : null;

        const response = await fetch(endpoint, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load order details');
        }

        const data = await response.json();
        setOrder(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Unable to load order details');
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [paymentIntentId, sessionId, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-700 mx-auto mb-4" />
          <p className="text-ink-soft">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="font-serif text-2xl font-bold text-ink mb-2">Order Error</h2>
          <p className="text-ink-soft mb-6">{error}</p>
          <Link
            to="/shop"
            className="inline-block bg-sage-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-sage-800 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Message */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Order Confirmed!</h1>
          <p className="text-ink-soft mb-4">
            Thank you for your purchase. Your order has been successfully placed.
          </p>
          {order && (
            <div className="bg-cream-100 rounded-lg p-4 inline-block">
              <p className="text-sm text-ink-muted">Order Number</p>
              <p className="text-xl font-bold text-sage-700">{order.orderNumber || order.id}</p>
            </div>
          )}
        </div>

        {/* Order Details */}
        {order && (
          <>
            {/* Shipping Information */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="font-serif text-xl font-bold text-ink mb-4">Shipping Information</h2>
              <div className="text-ink-soft">
                <p className="font-medium text-ink">{order.shippingAddress?.name}</p>
                <p>{order.shippingAddress?.street || order.shippingAddress?.address}</p>
                <p>
                  {order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zip}
                </p>
                {order.shippingAddress?.phone && <p className="mt-2">{order.shippingAddress.phone}</p>}
                {order.customerEmail && <p className="mt-1">{order.customerEmail}</p>}
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="font-serif text-xl font-bold text-ink mb-4">Order Items</h2>
              <div className="space-y-4">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center border-b pb-4 last:border-b-0 last:pb-0">
                    <div className="flex-1">
                      <p className="font-medium text-ink">{item.description || item.name}</p>
                      <p className="text-sm text-ink-muted">Quantity: {item.quantity}</p>
                    </div>
                    <p className="font-medium text-ink">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-soft">Subtotal</span>
                  <span className="font-medium text-ink">
                    ${order.subtotal ? (order.subtotal / 100).toFixed(2) : '0.00'}
                  </span>
                </div>
                {order.tax && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-soft">Tax</span>
                    <span className="font-medium text-ink">
                      ${(order.tax / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span className="text-ink">Total Paid</span>
                  <span className="text-sage-700">
                    ${order.total ? (order.total / 100).toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
            </div>

            {/* What's Next */}
            <div className="bg-sage-50 border border-sage-200 rounded-lg p-6 mb-6">
              <h2 className="font-serif text-xl font-bold text-ink mb-3">What happens next?</h2>
              <ul className="space-y-2 text-ink-soft">
                <li className="flex items-start">
                  <span className="text-sage-700 mr-2">✓</span>
                  <span>You'll receive a confirmation email shortly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sage-700 mr-2">✓</span>
                  <span>We'll prepare your order for shipment</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sage-700 mr-2">✓</span>
                  <span>You'll get a tracking number once your order ships</span>
                </li>
                {user && (
                  <li className="flex items-start">
                    <span className="text-sage-700 mr-2">✓</span>
                    <span>Track your order anytime in your account dashboard</span>
                  </li>
                )}
              </ul>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/shop"
            className="bg-sage-700 text-white px-8 py-3 rounded-lg font-medium hover:bg-sage-800 transition-colors text-center"
          >
            Continue Shopping
          </Link>
          {user && (
            <Link
              to="/account"
              className="bg-white text-sage-700 border-2 border-sage-700 px-8 py-3 rounded-lg font-medium hover:bg-sage-50 transition-colors text-center"
            >
              View My Orders
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
